/* eslint-disable no-empty */
import mixpanel from 'mixpanel-browser';
import { getLocalData } from 'utils/localDataStore';
import _get from 'lodash/get';
import _isUndefined from 'lodash/isUndefined';
import _isEmpty from 'lodash/isEmpty';
import { getFirebase } from 'react-redux-firebase';
import qs from 'qs';
import { emitter } from '@marvelapp/react-ab-test';
import GA from 'utils/gaService';
import Config from 'utils/getEnvConfig';
import { isBot, isLocalStorageAllowed } from 'utils/stringUtils';
import { isLocalhost } from 'utils/networkUtils';
import { getStore } from 'configureStore';
import FirebaseAnalyticsService from './firebaseAnalyticsService';

function registerTouch() {
  const touchURL = document.URL;

  mixpanel.people.set_once('First Touch URL', touchURL);
  mixpanel.people.set('Last Touch URL', touchURL);
}

function getReferrerGroup() {
  let referrerGroup = 'Other';

  if (/((http(s)?)?:\/\/)?google.([^/?]*)/.test(document.referrer)) {
    const params = qs.parse(document.URL.split('?')[1]);
    if (params.utm_source === 'adwords') referrerGroup = 'Google Paid';
    else referrerGroup = 'Google Search';
  } else if (/((http(s)?)?:\/\/)?bing.([^/?]*)/.test(document.referrer)) {
    referrerGroup = 'Bing';
  } else if (/((http(s)?)?:\/\/)?yahoo.([^/?]*)/.test(document.referrer)) {
    referrerGroup = 'Yahoo';
  } else if (
    /((http(s)?)?:\/\/)?facebook.([^/?]*)/.test(document.referrer) ||
    /((http(s)?)?:\/\/)?fb.([^/?]*)/.test(document.referrer)
  ) {
    referrerGroup = 'Facebook';
  } else if (
    /((http(s)?)?:\/\/)?twitter.([^/?]*)/.test(document.referrer) ||
    /((http(s)?)?:\/\/)?t.co/.test(document.referrer)
  ) {
    referrerGroup = 'Twitter';
  } else if (/((http(s)?)?:\/\/)?linkedin.([^/?]*)/.test(document.referrer)) {
    referrerGroup = 'LinkedIn';
  } else if (/((http(s)?)?:\/\/)?quora.([^/?]*)/.test(document.referrer)) {
    referrerGroup = 'Quora';
  } else if (/((http(s)?)?:\/\/)?duckduckgo.([^/?]*)/.test(document.referrer)) {
    referrerGroup = 'DuckDuckGo';
  }

  mixpanel.people.set_once('Initial Referrer Domain Group', referrerGroup);
}

function getUtmTags() {
  const params = qs.parse(document.URL.split('?')[1]);

  const firstTouch = {};
  const lastTouch = {
    'utm_source [last touch]': '$direct',
    'utm_campaign [last touch]': undefined,
  };

  if (params.utm_source) {
    firstTouch['utm_source [first touch]'] = params.utm_source;
    lastTouch['utm_source [last touch]'] = params.utm_source;
  }
  if (params.utm_campaign) {
    firstTouch['utm_campaign [first touch]'] = params.utm_campaign;
    lastTouch['utm_campaign [last touch]'] = params.utm_campaign;
  }
  mixpanel.people.set_once(firstTouch);
  mixpanel.people.set(lastTouch);
  mixpanel.register(lastTouch);
}

function registerUserAgentWithBotIgnore() {
  const { userAgent } = navigator;
  mixpanel.register({ 'User Agent': userAgent });

  if (isBot()) {
    mixpanel.register({ $ignore: true });
  }
}

// enable only in production mode
const activeMixpanel = !isLocalhost && !isBot();

if (activeMixpanel) {
  mixpanel.init(Config.MIXPANEL.TOKEN, {
    api_host: 'https://userdata.crediblemind.com',
    ignore_dnt: true,
  });
  registerUserAgentWithBotIgnore();
  registerTouch();
  getReferrerGroup();
  getUtmTags();
}

let identified = false;

const identify = async () => {
  const isAICC = await getIsAICC();
  if (isAICC) {
    await waitForAICCData();
  }

  const firebase = await getFirebase();
  const store = getStore();
  const state = store.getState();
  const auth = _get(state, 'firebase.auth');
  const aiccUser = _get(state, 'main.aiccUser', {});
  const sessionAnonymousId = _get(state, 'auth.anonymousId');
  const id =
    _get(aiccUser, 'user_id') ||
    _get(auth, 'uid') ||
    getLocalData('userIdTemp') ||
    sessionAnonymousId;
  if (activeMixpanel) {
    // console.log('mixpanel.identify', { id, auth, aiccUser });
    mixpanel.identify(id);
    identified = true;
    firebase.analytics().setUserId(id);
  }
};

let brandSector;
export const setBrandSector = sector => {
  brandSector = sector;
};

let brandParent;
export const setBrandParent = parent => {
  brandParent = parent;
};

const getProfile = store => {
  const state = store.getState();
  if (!_get(state, 'firebase.profile.isLoaded')) return undefined;
  return _get(state, 'firebase.profile', 'null');
};

const getClientDetails = store => {
  const state = store.getState();
  return _get(state, 'main.clientDetails', 'null');
};

const getAICCUser = store => {
  const state = store.getState();
  return _get(state, 'main.aiccUser', {});
};

const getIsInternal = async () =>
  new Promise(resolve => {
    const store = getStore();
    let profile = getProfile(store);
    if (profile !== undefined) {
      const isInternal =
        profile.role === 'admin' ||
        profile.role === 'contentAdmin' ||
        (profile.email || '').includes('@crediblemind.com');
      resolve(isInternal);
    } else {
      const unsubscribe = store.subscribe(() => {
        profile = getProfile(store);
        if (profile !== undefined) {
          unsubscribe();
          const isInternal =
            profile.role === 'admin' ||
            profile.role === 'contentAdmin' ||
            (profile.email || '').includes('@crediblemind.com');
          resolve(isInternal);
        }
      });
    }
  });

const getProfileData = async () =>
  new Promise(resolve => {
    const store = getStore();
    let profile = getProfile(store);
    if (profile !== undefined) {
      const data = {};
      if (profile.gender) {
        data.userGender = profile.gender;
      }
      if (profile.dob) {
        data.userAge = profile.dob;
      }
      if (profile.industry) {
        data.userIndustry = profile.industry;
      }
      if (!_isEmpty(profile.insurance) || getLocalData('insurance')) {
        if (!_isEmpty(profile.insurance)) {
          if (Array.isArray(profile.insurance))
            data.userInsurance = profile.insurance.join(',');
          else data.userInsurance = profile.insurance;
        } else {
          data.userInsurance = getLocalData('insurance');
        }
      }
      resolve(data);
    } else {
      const unsubscribe = store.subscribe(() => {
        profile = getProfile(store);
        if (profile !== undefined) {
          unsubscribe();
          const data = {};
          if (profile.gender) {
            data.gender = profile.gender;
          }
          if (profile.dob) {
            data.age = profile.dob;
          }
          if (profile.industry) {
            data.userIndustry = profile.industry;
          }
          if (profile.insurance || getLocalData('insurance')) {
            if (profile.insurance) {
              if (Array.isArray(profile.insurance))
                data.userInsurance = profile.insurance.join(',');
              else data.userInsurance = profile.insurance;
            } else {
              data.userInsurance = getLocalData('insurance');
            }
          }
          resolve(data);
        }
      });
    }
  });

const getIsAICC = async () =>
  new Promise(resolve => {
    const store = getStore();
    let clientDetails = getClientDetails(store);
    if (clientDetails !== undefined) {
      const isAICC = _get(clientDetails, 'aicc', false);
      resolve(isAICC);
    } else {
      const unsubscribe = store.subscribe(() => {
        clientDetails = getClientDetails(store);
        if (clientDetails !== undefined) {
          unsubscribe();
          const isAICC = _get(clientDetails, 'aicc', false);
          resolve(isAICC);
        }
      });
    }
  });

const getAICCData = async () =>
  new Promise(resolve => {
    const store = getStore();
    let aiccUser = getAICCUser(store);
    if (aiccUser !== undefined) {
      const data = {
        organizationName: _get(aiccUser, 'organization_name'),
        institutionName: _get(aiccUser, 'institution_name'),
        studentId: _get(aiccUser, 'student_id'),
      };
      resolve(data);
    } else {
      const unsubscribe = store.subscribe(() => {
        aiccUser = getAICCUser(store);
        if (aiccUser !== undefined) {
          unsubscribe();
          const data = {
            organizationName: _get(aiccUser, 'organization_name'),
            institutionName: _get(aiccUser, 'institution_name'),
            studentId: _get(aiccUser, 'student_id'),
          };
          resolve(data);
        }
      });
    }
  });

const waitForAICCData = async () =>
  new Promise(resolve => {
    const store = getStore();
    let user = getAICCUser(store);
    if (!_isEmpty(user)) {
      resolve(true);
    } else {
      const unsubscribe = store.subscribe(() => {
        user = getAICCUser(store);
        if (!_isEmpty(user)) {
          unsubscribe();
          resolve(true);
        }
      });
    }
  });

const getIsSignedIn = async () =>
  new Promise(resolve => {
    const store = getStore();
    let profile = getProfile(store);
    // console.log('getIsSignedIn', profile);
    if (profile !== undefined) {
      const isSignedIn = !profile.isEmpty;
      resolve(isSignedIn);
    } else {
      const unsubscribe = store.subscribe(() => {
        profile = getProfile(store);
        // console.log('getIsSignedIn', profile);
        if (profile !== undefined) {
          unsubscribe();
          const isSignedIn = !profile.isEmpty;
          resolve(isSignedIn);
        }
      });
    }
  });

const MixpanelActions = {
  // init: token => {
  //   if (activeMixpanel) {
  //     mixpanel.init(token);
  //   }
  // },
  identify,
  alias: firebaseId => {
    const id = getLocalData('userIdTemp');
    // console.log('mixpanel.alias', firebaseId, id);
    if (activeMixpanel) mixpanel.alias(firebaseId, id);
  },
  track: async (name, props) => {
    if (!identified) {
      await identify();
    }

    const heroVariant = emitter.getActiveVariant('hero-variant');
    const assessmentResultsVariant = emitter.getActiveVariant(
      'assessment-results-lists',
    );
    const firstTimeUser = getLocalData('firstTimeUser');
    let firstTimeUserFinal = 'Returning';
    if (_isUndefined(firstTimeUser)) {
      firstTimeUserFinal = 'Undefined';
    } else if (firstTimeUser === 'true') {
      firstTimeUserFinal = 'New';
    }
    const brand = getLocalData('brand');
    const profileData = await getProfileData();
    const isInternal = await getIsInternal();
    const isSignedIn = await getIsSignedIn();
    const isAICC = await getIsAICC();
    const aiccData = isAICC ? await getAICCData() : {};
    let language = 'Undefined';
    if (isLocalStorageAllowed()) {
      language = localStorage.getItem('language');
      if (language === null) language = 'english';
    }

    const finalProps = {
      ...props,
      brand: brand || 'none',
      brandSector,
      brandParent,
      firstTimeUser: firstTimeUserFinal,
      homeVariant: heroVariant || 'none',
      assessmentResultsVariant: assessmentResultsVariant || 'none',
      isInternal,
      isSignedIn,
      isAICC,
      ...aiccData,
      ...profileData,
      language,
    };
    if (!finalProps.name) finalProps.name = name;

    // console.log('mixpanel.track', name, finalProps);
    if (activeMixpanel) {
      mixpanel.track(name, finalProps);
      GA.track(name, finalProps);
      FirebaseAnalyticsService.addEvent(name, finalProps);
    }
  },
  // session_end: userId => {
  //   if (activeMixpanel) {
  //     const token = Config.MIXPANEL.TOKEN;
  //     const startTime =
  //       // eslint-disable-next-line dot-notation
  //       mixpanel['persistence']['props']['__timers']['Session ended'];
  //     let duration = new Date().getTime() - startTime;
  //     duration = parseFloat((duration / 1000).toFixed(3));
  //     const data = {
  //       event: 'Session ended',
  //       properties: {
  //         ...mixpanel._.info.properties(),
  //         ...mixpanel.persistence.properties(),
  //         token,
  //         distinct_id: userId,
  //         $duration: duration,
  //         name: 'Session ended',
  //       },
  //     };
  //     const encodedData = btoa(JSON.stringify(data));

  //     const req = new XMLHttpRequest();
  //     req.open(
  //       'GET',
  //       `https://api.mixpanel.com/track/?data=${encodedData}&ip=1&_=${Date.now()}`,
  //       false,
  //     );
  //     const headers = mixpanel.get_config('xhr_headers');
  //     _forEach(headers, (headerValue, headerName) => {
  //       req.setRequestHeader(headerName, headerValue);
  //     });
  //     req.withCredentials = true;
  //     req.send(null);
  //   }
  // },
  // time_event: name => {
  //   if (activeMixpanel) mixpanel.time_event(name);
  // },
  people: {
    set: props => {
      const brand = getLocalData('brand');
      if (activeMixpanel) {
        // console.log('mixpanel.people.set');
        mixpanel.people.set(props);
        FirebaseAnalyticsService.setUserProfile({ ...props, brand });
      }
    },
  },
  reset: () => {
    identified = false;
    if (activeMixpanel) mixpanel.reset();
  },
  forceIdentify: () => {
    identified = false;
  },
};

export default MixpanelActions;
